import React from 'react';
import {
  Users, Clock, CheckCircle, CreditCard, XCircle, TrendingUp, AlertTriangle,
  Mail, FileText, ArrowRight, BellRing, DollarSign, Sparkles
} from 'lucide-react';

const copy = {
  fr: {
    welcome: "Bonjour",
    subtitle: "Voici un aperçu de votre conférence aujourd'hui",
    kpi: {
      total: "Total inscriptions",
      pending: "En attente",
      approved: "Approuvées",
      paid: "Payées",
      exempted: "Exemptées",
      rejected: "Rejetées",
      revenue: "Revenu estimé",
    },
    needsAttention: "À traiter",
    needsAttentionSub: "Actions recommandées en priorité",
    pendingApprovals: "Inscriptions en attente d'approbation",
    unpaidApproved: "Approuvées sans paiement",
    invitationsPending: "Invitations visa en attente",
    noAttention: "Rien à traiter pour le moment. Bon travail !",
    recentActivity: "Activité récente",
    noActivity: "Aucune activité récente",
    quickActions: "Actions rapides",
    actions: {
      reviewPending: "Examiner les inscriptions en attente",
      reviewPendingSub: "Approuver ou rejeter les nouvelles demandes",
      sendReminders: "Envoyer rappels de paiement",
      sendRemindersSub: "Dispatcher le lot automatique maintenant",
      ottawaTeaser: "Campagne \"Saviez-vous ?\" Ottawa",
      ottawaTeaserSub: "Email pro d'astuces pour préparer leur voyage",
      postponement: "Annoncer le report (Mars 2027)",
      postponementSub: "Prévisualiser & envoyer l’annonce du report",
      downloadProgram: "Télécharger le programme (PDF)",
      downloadProgramSub: "Version maître (sans participant ni numéro)",
      manageProgram: "Organiser le programme",
      manageProgramSub: "Mettre à jour l'agenda par jour",
    },
    goTo: "Accéder",
    viewAll: "Voir tout",
    teaserAllConfirm: "Envoyer la campagne « Saviez-vous ? » Ottawa à TOUS les utilisateurs (inscrits, approuvés, payés) ?",
    teaserUnpaidConfirm: "Envoyer uniquement aux utilisateurs APPROUVÉS mais NON PAYÉS ?",
    teaserChooseAudience: "Choisissez l'audience :\n\n1 — Tous les utilisateurs (recommandé)\n2 — Uniquement les non payés (avec rappel de paiement)\n\nTapez 1 ou 2 :",
  },
  en: {
    welcome: "Hello",
    subtitle: "Here's a snapshot of your conference today",
    kpi: {
      total: "Total registrations",
      pending: "Pending",
      approved: "Approved",
      paid: "Paid",
      exempted: "Exempted",
      rejected: "Rejected",
      revenue: "Est. revenue",
    },
    needsAttention: "Needs attention",
    needsAttentionSub: "Recommended priority actions",
    pendingApprovals: "Registrations awaiting approval",
    unpaidApproved: "Approved but unpaid",
    invitationsPending: "Visa invitations pending",
    noAttention: "Nothing urgent. Great job!",
    recentActivity: "Recent activity",
    noActivity: "No recent activity",
    quickActions: "Quick actions",
    actions: {
      reviewPending: "Review pending registrations",
      reviewPendingSub: "Approve or reject new requests",
      sendReminders: "Dispatch payment reminders",
      sendRemindersSub: "Run the automatic batch now",
      ottawaTeaser: "\"Did You Know?\" Ottawa Campaign",
      ottawaTeaserSub: "Pro email with insider travel tips",
      postponement: "Announce Postponement (March 2027)",
      postponementSub: "Preview & send the postponement announcement",
      downloadProgram: "Download Program (PDF)",
      downloadProgramSub: "Master copy (no attendee, no invitation number)",
      manageProgram: "Organize program",
      manageProgramSub: "Update the daily agenda",
    },
    goTo: "Open",
    viewAll: "View all",
    teaserAllConfirm: "Send the 'Did You Know?' Ottawa campaign to ALL users (pending, approved, paid)?",
    teaserUnpaidConfirm: "Send only to APPROVED but UNPAID users?",
    teaserChooseAudience: "Choose audience:\n\n1 — All users (recommended)\n2 — Only unpaid users (with payment reminder)\n\nType 1 or 2:",
  },
};

function relativeTime(iso, lang) {
  if (!iso) return '';
  try {
    const then = new Date(iso);
    const secs = Math.max(1, Math.floor((Date.now() - then.getTime()) / 1000));
    const map = lang === 'fr'
      ? [[86400, 'j'], [3600, 'h'], [60, 'min'], [1, 's']]
      : [[86400, 'd'], [3600, 'h'], [60, 'm'], [1, 's']];
    for (const [d, u] of map) {
      if (secs >= d) return `${Math.floor(secs / d)}${u}`;
    }
    return '';
  } catch (e) {
    return '';
  }
}

export default function AdminOverview({
  language = 'fr',
  user,
  stats = {},
  registrations = [],
  onNavigate,
  onDispatchReminders,
  onSendOttawaTeaser,
  onOpenPostponement,
  onDownloadProgram,
  invitationsPendingCount = 0,
  isSubAdmin = false,
}) {
  const t = copy[language] || copy.fr;

  const unpaidApproved = (registrations || []).filter((r) => r.status === 'approved' && !r.payment_exempted).length;
  const estimatedRevenue = (stats.revenue !== undefined ? stats.revenue : (stats.paid || 0) * 499).toLocaleString(language === 'fr' ? 'fr-CA' : 'en-US');

  const allKpis = [
    { k: 'total', icon: Users, color: 'from-blue-500 to-blue-600', value: stats.total || 0 },
    { k: 'pending', icon: Clock, color: 'from-amber-500 to-orange-500', value: stats.pending || 0 },
    { k: 'approved', icon: CheckCircle, color: 'from-emerald-500 to-green-600', value: stats.approved || 0 },
    { k: 'paid', icon: CreditCard, color: 'from-teal-500 to-cyan-600', value: stats.paid || 0 },
    { k: 'exempted', icon: Sparkles, color: 'from-purple-500 to-fuchsia-600', value: stats.exempted || 0 },
    { k: 'rejected', icon: XCircle, color: 'from-rose-500 to-red-600', value: stats.rejected || 0 },
    { k: 'revenue', icon: DollarSign, color: 'from-[#CD0000] to-[#FF3333]', value: `$${estimatedRevenue}`, isRevenue: true },
  ];

  // Sub-admins do NOT see exempted users (those are paid via comp).
  const kpis = isSubAdmin ? allKpis.filter((k) => k.k !== 'exempted') : allKpis;

  const attentionItems = [
    stats.pending > 0 && { icon: Clock, count: stats.pending, label: t.pendingApprovals, tone: 'amber', tab: 'registrations' },
    unpaidApproved > 0 && { icon: BellRing, count: unpaidApproved, label: t.unpaidApproved, tone: 'orange', tab: 'registrations' },
    !isSubAdmin && invitationsPendingCount > 0 && { icon: Mail, count: invitationsPendingCount, label: t.invitationsPending, tone: 'pink', tab: 'invitations' },
  ].filter(Boolean);

  const recent = [...(registrations || [])]
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
    .slice(0, 8);

  const allActions = [
    { icon: Clock, bg: 'bg-amber-50', fg: 'text-amber-600', title: t.actions.reviewPending, sub: t.actions.reviewPendingSub, onClick: () => onNavigate('registrations') },
    { icon: BellRing, bg: 'bg-orange-50', fg: 'text-orange-600', title: t.actions.sendReminders, sub: t.actions.sendRemindersSub, onClick: onDispatchReminders, adminOnly: true },
    { icon: Mail, bg: 'bg-rose-50', fg: 'text-[#CD0000]', title: t.actions.postponement, sub: t.actions.postponementSub, onClick: onOpenPostponement, adminOnly: true },
    { icon: FileText, bg: 'bg-amber-50', fg: 'text-[#D4AF37]', title: t.actions.downloadProgram, sub: t.actions.downloadProgramSub, onClick: onDownloadProgram },
    { icon: Sparkles, bg: 'bg-red-50', fg: 'text-[#CD0000]', title: t.actions.ottawaTeaser, sub: t.actions.ottawaTeaserSub, onClick: onSendOttawaTeaser, adminOnly: true },
    { icon: FileText, bg: 'bg-emerald-50', fg: 'text-emerald-600', title: t.actions.manageProgram, sub: t.actions.manageProgramSub, onClick: () => onNavigate('manageProgram'), adminOnly: true },
  ];
  const actions = isSubAdmin ? allActions.filter((a) => !a.adminOnly) : allActions;

  return (
    <div className="space-y-6" data-testid="admin-overview">
      {/* Greeting */}
      <div className="rounded-2xl bg-gradient-to-br from-[#1D1D1D] via-[#2a1220] to-[#0B0E13] p-6 md:p-7 text-white shadow-xl overflow-hidden relative">
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[#CD0000]/20 blur-3xl pointer-events-none" />
        <div className="relative flex items-start justify-between gap-4">
          <div>
            <p className="text-[12px] font-semibold text-[#FF3333] uppercase tracking-[0.16em] mb-1.5 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> {t.welcome}
            </p>
            <h2 className="text-2xl md:text-3xl font-extrabold leading-tight">
              {user?.first_name || 'Admin'} &mdash; {new Date().toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </h2>
            <p className="text-gray-400 text-sm mt-1.5">{t.subtitle}</p>
          </div>
          <div className="hidden md:flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-4 py-3">
            <TrendingUp className="w-5 h-5 text-emerald-400" />
            <div>
              <p className="text-[10px] text-gray-400 uppercase tracking-wider">{t.kpi.revenue}</p>
              <p className="text-lg font-extrabold text-white">${estimatedRevenue}</p>
            </div>
          </div>
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3">
        {kpis.map((k) => (
          <div
            key={k.k}
            data-testid={`admin-kpi-${k.k}`}
            className="bg-white rounded-xl border border-gray-200/70 p-4 hover:border-gray-300 hover:shadow-sm transition group"
          >
            <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${k.color} flex items-center justify-center mb-3 shadow-sm`}>
              <k.icon className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">{t.kpi[k.k]}</p>
            <p className={`font-extrabold text-[#1D1D1D] leading-tight ${k.isRevenue ? 'text-lg mt-0.5' : 'text-2xl mt-0.5'}`}>
              {k.value}
            </p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Needs Attention */}
        <div className="lg:col-span-1 bg-white rounded-xl border border-gray-200/70 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            <div>
              <h3 className="font-extrabold text-[14px] text-[#1D1D1D]">{t.needsAttention}</h3>
              <p className="text-[11px] text-gray-500">{t.needsAttentionSub}</p>
            </div>
          </div>
          <div className="p-3">
            {attentionItems.length === 0 ? (
              <div className="text-center py-8 text-sm text-gray-400">
                <CheckCircle className="w-10 h-10 mx-auto text-emerald-400 mb-2" />
                {t.noAttention}
              </div>
            ) : (
              <div className="space-y-2">
                {attentionItems.map((a, i) => {
                  const toneBg = a.tone === 'amber' ? 'bg-amber-50 hover:bg-amber-100 border-amber-100' : a.tone === 'orange' ? 'bg-orange-50 hover:bg-orange-100 border-orange-100' : 'bg-red-50 hover:bg-red-100 border-red-100';
                  const toneFg = a.tone === 'amber' ? 'text-amber-600' : a.tone === 'orange' ? 'text-orange-600' : 'text-[#CD0000]';
                  return (
                    <button
                      key={i}
                      onClick={() => onNavigate(a.tab)}
                      data-testid={`admin-attention-${a.tone}`}
                      className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg border ${toneBg} transition text-left`}
                    >
                      <a.icon className={`w-5 h-5 ${toneFg} shrink-0`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-[#1D1D1D]">{a.count} {a.label}</p>
                      </div>
                      <ArrowRight className={`w-4 h-4 ${toneFg}`} />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Quick actions */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200/70 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h3 className="font-extrabold text-[14px] text-[#1D1D1D]">{t.quickActions}</h3>
          </div>
          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {actions.map((a, i) => (
              <button
                key={i}
                onClick={a.onClick}
                data-testid={`admin-quickaction-${i}`}
                className="group flex items-center gap-3 p-4 rounded-xl border border-gray-200/70 hover:border-[#CD0000]/40 hover:bg-gray-50 transition text-left"
              >
                <div className={`w-10 h-10 rounded-lg ${a.bg} flex items-center justify-center shrink-0`}>
                  <a.icon className={`w-5 h-5 ${a.fg}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-[#1D1D1D] truncate">{a.title}</p>
                  <p className="text-[12px] text-gray-500 truncate">{a.sub}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-300 group-hover:text-[#CD0000] group-hover:translate-x-0.5 transition shrink-0" />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Recent activity */}
      <div className="bg-white rounded-xl border border-gray-200/70 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-extrabold text-[14px] text-[#1D1D1D]">{t.recentActivity}</h3>
          <button
            onClick={() => onNavigate('registrations')}
            className="text-[12px] font-bold text-[#CD0000] hover:underline flex items-center gap-1"
          >
            {t.viewAll} <ArrowRight className="w-3 h-3" />
          </button>
        </div>
        {recent.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-400">{t.noActivity}</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {recent.map((r) => (
              <li
                key={r.id}
                data-testid={`admin-recent-${r.id}`}
                className="px-5 py-3 flex items-center gap-3 hover:bg-gray-50 transition cursor-pointer"
                onClick={() => onNavigate('registrations')}
              >
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#1D1D1D] to-[#444] flex items-center justify-center text-white text-[11px] font-bold shrink-0">
                  {(r.user?.first_name || '?')[0]}{(r.user?.last_name || '')[0] || ''}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#1D1D1D] truncate">
                    {r.user?.first_name} {r.user?.last_name}
                  </p>
                  <p className="text-[12px] text-gray-500 truncate">{r.user?.email}</p>
                </div>
                <StatusPill status={r.status} lang={language} exempted={r.payment_exempted} />
                <span className="text-[11px] text-gray-400 hidden md:inline tabular-nums shrink-0 w-10 text-right">
                  {relativeTime(r.created_at, language)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function StatusPill({ status, lang, exempted }) {
  const map = {
    pending: { fr: 'En attente', en: 'Pending', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    approved: { fr: 'Approuvée', en: 'Approved', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    paid: { fr: 'Payée', en: 'Paid', cls: 'bg-teal-50 text-teal-700 border-teal-200' },
    rejected: { fr: 'Rejetée', en: 'Rejected', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
    incomplete: { fr: 'Incomplète', en: 'Incomplete', cls: 'bg-gray-100 text-gray-700 border-gray-200' },
  };
  const meta = map[status] || { fr: status, en: status, cls: 'bg-gray-50 text-gray-600 border-gray-200' };
  const label = exempted ? (lang === 'fr' ? 'Exemptée' : 'Exempted') : meta[lang === 'fr' ? 'fr' : 'en'];
  const cls = exempted ? 'bg-purple-50 text-purple-700 border-purple-200' : meta.cls;
  return (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md border ${cls} shrink-0`}>
      {label}
    </span>
  );
}
